import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Users, MapPin, Save, ArrowLeft } from 'lucide-react';
import { getPositionsForSport } from '@/data/sportsPositions';

interface GameRoster {
  id: string;
  user_id: string;
  position?: string;
  user_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    avatar_url?: string;
  };
}

interface GamePositionManagerProps {
  gameId: string;
  gameCode: string;
  gameTitle: string;
  teamSide: 'home' | 'away';
  onBack: () => void;
}

const getPositionOptions = (sportName?: string): string[] => {
  console.log('🏈 Getting position options for sport:', sportName);
  
  if (!sportName) return getPositionsForSport('Football'); // Default to American Football
  
  // Use the sports positions data to get the correct positions
  return getPositionsForSport(sportName);
};

export const GamePositionManager: React.FC<GamePositionManagerProps> = ({
  gameId,
  gameCode,
  gameTitle,
  teamSide,
  onBack
}) => {
  const [roster, setRoster] = useState<GameRoster[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [positions, setPositions] = useState<Record<string, string>>({});
  const [gameData, setGameData] = useState<{ sport_name?: string; sport_id?: number; home_team_name?: string; away_team_name?: string } | null>(null);

  useEffect(() => {
    console.log('🎯 GamePositionManager mounted with:', { gameId, teamSide, gameCode, gameTitle });
    if (gameId && teamSide) {
      loadGameRoster();
      loadGameData();
    }
  }, [gameId, teamSide]);

  const loadGameRoster = async () => {
    try {
      console.log('🎯 Loading game roster for gameId:', gameId, 'teamSide:', teamSide);
      setLoading(true);
      
      // Load game roster for this team side
      const { data: rosterData, error: rosterError } = await supabase
        .from('game_rosters')
        .select('id, player_id, position')
        .eq('game_id', gameId)
        .eq('team_side', teamSide);

      console.log('🎯 Roster query result:', { rosterData, rosterError });
      if (rosterError) throw rosterError;

      if (!rosterData || rosterData.length === 0) {
        console.log('🎯 No roster data found, setting empty roster');
        setRoster([]);
        return;
      }

      // Get user details for all roster members from both tables
      const userIds = rosterData.map(r => r.player_id);
      
      // Get Supabase profiles
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name')
        .in('id', userIds);

      // Get internal users
      const { data: internalUserData } = await supabase
        .from('internal_users')
        .select('id, username')
        .in('id', userIds);

      // Combine roster data with user details
      const rosterWithDetails = rosterData.map(rosterItem => {
        const profileUser = profileData?.find(p => p.id === rosterItem.player_id);
        const internalUser = internalUserData?.find(u => u.id === rosterItem.player_id);
        
        let userDetails = {};
        if (profileUser) {
          userDetails = {
            id: profileUser.id,
            username: profileUser.username,
            first_name: profileUser.first_name,
            last_name: profileUser.last_name,
            user_type: 'supabase'
          };
        } else if (internalUser) {
          userDetails = {
            id: internalUser.id,
            username: internalUser.username,
            first_name: '',
            last_name: '',
            user_type: 'internal'
          };
        }

        return {
          ...rosterItem,
          user_id: rosterItem.player_id, // Map player_id to user_id for consistency
          user_details: userDetails
        };
      });

      setRoster(rosterWithDetails);
      
      // Initialize positions state
      const initialPositions: Record<string, string> = {};
      rosterData.forEach(item => {
        if (item.position) {
          initialPositions[item.player_id] = item.position;
        }
      });
      setPositions(initialPositions);

    } catch (error) {
      console.error('🚨 Error loading game roster:', error);
      toast.error('Failed to load game roster');
    } finally {
      setLoading(false);
    }
  };

  const loadGameData = async () => {
    try {
      // First try venue_games table
      const { data: venueGame } = await supabase
        .from('venue_games')
        .select(`
          *,
          available_sports (sport_name),
          home_team:teams!venue_games_home_team_id_fkey (team_name),
          away_team:teams!venue_games_away_team_id_fkey (team_name)
        `)
        .eq('id', gameId)
        .single();

      if (venueGame && venueGame.available_sports) {
        console.log('🎯 Venue game data loaded:', venueGame);
        setGameData({ 
          sport_name: venueGame.available_sports.sport_name,
          sport_id: venueGame.sport_id,
          home_team_name: venueGame.home_team?.team_name,
          away_team_name: venueGame.away_team?.team_name
        });
        return;
      }

      // Try regular games table if venue_games doesn't work
      const { data: regularGame } = await supabase
        .from('games')
        .select(`
          *,
          available_sports (sport_name),
          home_team:teams!games_home_team_id_fkey (team_name),
          away_team:teams!games_away_team_id_fkey (team_name)
        `)
        .eq('id', gameId)
        .single();

      if (regularGame && regularGame.available_sports) {
        console.log('🎯 Regular game data loaded:', regularGame);
        setGameData({ 
          sport_name: regularGame.available_sports.sport_name,
          sport_id: regularGame.sport_id,
          home_team_name: regularGame.home_team?.team_name,
          away_team_name: regularGame.away_team?.team_name
        });
      }
    } catch (error) {
      console.error('Error loading game data:', error);
    }
  };

  const handlePositionChange = (userId: string, position: string) => {
    setPositions(prev => ({
      ...prev,
      [userId]: position
    }));
  };

  const handleSavePositions = async () => {
    try {
      setSaving(true);

      // Update positions for each roster member
      const updates = Object.entries(positions).map(([userId, position]) => {
        return supabase
          .from('game_rosters')
          .update({ position })
          .eq('game_id', gameId)
          .eq('team_side', teamSide)
          .eq('player_id', userId);
      });

      await Promise.all(updates);
      
      toast.success('Player positions saved successfully!');
    } catch (error) {
      console.error('Error saving positions:', error);
      toast.error('Failed to save positions');
    } finally {
      setSaving(false);
    }
  };

  const getPlayerDisplayName = (player: GameRoster) => {
    const details = player.user_details;
    if (details?.username) return details.username;
    if (details?.first_name && details?.last_name) {
      return `${details.first_name} ${details.last_name}`;
    }
    if (details?.first_name) return details.first_name;
    return 'Unknown Player';
  };

  const getPlayerInitials = (player: GameRoster) => {
    const details = player.user_details;
    if (details?.first_name && details?.last_name) {
      return `${details.first_name[0]}${details.last_name[0]}`;
    }
    if (details?.username) return details.username.slice(0, 2).toUpperCase();
    return 'UP';
  };

  if (loading) {
    console.log('🎯 Still loading roster...');
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="text-muted-foreground">Loading roster...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  console.log('🎯 Rendering GamePositionManager with roster:', roster, 'loading:', loading);
  
  if (!gameId || !teamSide) {
    console.log('🚨 Missing required props:', { gameId, teamSide });
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-red-400">
            Error: Missing game information
          </div>
          <Button onClick={onBack} className="mt-4">
            Go Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1">
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Position Management
              </CardTitle>
              <div className="text-sm text-muted-foreground mt-1">
                {gameTitle && gameTitle !== 'Home Team vs Away Team' ? gameTitle : 'Position Management'} • Game Code: <Badge variant="outline">{gameCode}</Badge>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Team Side Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {teamSide === 'home' ? 'Home Team' : 'Away Team'} 
            {gameData && (
              <span className="text-muted-foreground">
                - {teamSide === 'home' ? gameData.home_team_name : gameData.away_team_name}
              </span>
            )} Positions
          </CardTitle>
        </CardHeader>
        <CardContent className="max-h-[60vh] overflow-y-auto">
          {roster.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No players assigned to this team yet.
            </div>
          ) : (
            <div className="space-y-4">
              {roster.map((player) => (
                <div key={player.id} className="flex items-center gap-4 p-4 border border-gray-700 rounded-lg">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={player.user_details?.avatar_url} />
                    <AvatarFallback className="bg-gray-700 text-white">{getPlayerInitials(player)}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1">
                    <div className="font-medium text-white">{getPlayerDisplayName(player)}</div>
                    <div className="text-sm text-gray-400">
                      {teamSide === 'home' ? 'Home Team' : 'Away Team'}
                    </div>
                  </div>

                  <div className="w-48">
                    <Select
                      value={positions[player.user_id] || 'none'}
                      onValueChange={(value) => handlePositionChange(player.user_id, value)}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-600">
                        <SelectValue placeholder="Select position" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Position</SelectItem>
                        {getPositionOptions(gameData?.sport_name).map((position) => (
                          <SelectItem key={position} value={position}>
                            {position}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}

              <Separator />

              <div className="flex justify-end">
                <Button 
                  onClick={handleSavePositions}
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Positions'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};